import React, { useState } from "react";
import {
  Alert,
  Container,
  Header,
  SpaceBetween,
} from "@amzn/awsui-components-react";

import { AsinWithCatalogIssues } from "../../../from-server/asin-central/catalog-issues";
import {
  CatalogIssueDetailState,
  CatalogIssuesSearchState,
  filterAsinsWithIssues,
} from "./useCatalogIssues";
import { CatalogIssuesSummary } from "./CatalogIssuesSummary";
import { CatalogIssuesTable } from "./CatalogIssuesTable";
import { CatalogIssueDetailPane } from "./CatalogIssueDetailPane";

export interface CatalogIssuesPanelProps {
  readonly search: CatalogIssuesSearchState;
  /** Detail state for each selected ASIN, in selection order. */
  readonly details: ReadonlyArray<CatalogIssueDetailState>;
  /** Currently checked rows (controlled selection). */
  readonly selectedItems: readonly AsinWithCatalogIssues[];
  readonly onSelectionChange: (items: AsinWithCatalogIssues[]) => void;
}

const UNAVAILABLE_FALLBACK_MESSAGE =
  "Catalog data could not be retrieved. Catalog issues are temporarily " +
  "unavailable; try again later.";

const LIMIT_EXCEEDED_FALLBACK_MESSAGE =
  "The current search matches too many ASINs to check for catalog issues. " +
  "Narrow your filter and try again.";

/**
 * Presentational body of the Catalog Issues tab. Branches on the fetch
 * status and, once loaded, on `dependencyStatus` (the BFF always answers
 * HTTP 200, even when the catalog dependency is degraded):
 *
 * - AVAILABLE       -> summary + table (+ lazy detail pane)
 * - PARTIAL         -> the same, plus a warning Alert from `warnings`
 * - UNAVAILABLE     -> an error Alert only; never looks like a crash
 * - LIMIT_EXCEEDED  -> a "narrow your filter" Alert, no misleading counts
 */
export const CatalogIssuesPanel: React.FC<CatalogIssuesPanelProps> = ({
  search,
  details,
  selectedItems,
  onSelectionChange,
}) => {
  const [filteringText, setFilteringText] = useState("");

  if (search.status === "failed") {
    return (
      <Container data-testid="catalog-issues-panel">
        <Alert type="error" data-testid="catalog-issues-fetch-error">
          Failed to load catalog issues. The rest of the dashboard is
          unaffected; try submitting the filter again.
        </Alert>
      </Container>
    );
  }

  if (search.status === "loaded" && search.result !== undefined) {
    if (search.result.dependencyStatus === "UNAVAILABLE") {
      return (
        <Container data-testid="catalog-issues-panel">
          <Alert
            type="error"
            header="Catalog data unavailable"
            data-testid="catalog-issues-unavailable-alert"
          >
            {search.result.warnings.length > 0
              ? search.result.warnings.join(" ")
              : UNAVAILABLE_FALLBACK_MESSAGE}
          </Alert>
        </Container>
      );
    }

    if (search.result.dependencyStatus === "LIMIT_EXCEEDED") {
      return (
        <Container data-testid="catalog-issues-panel">
          <Alert
            type="warning"
            header="Too many ASINs to check"
            data-testid="catalog-issues-limit-exceeded-alert"
          >
            {search.result.warnings.length > 0
              ? search.result.warnings.join(" ")
              : LIMIT_EXCEEDED_FALLBACK_MESSAGE}
          </Alert>
        </Container>
      );
    }
  }

  const rows = search.result?.asinsWithIssues ?? [];
  const filteredRows = filterAsinsWithIssues(rows, filteringText);

  return (
    <Container data-testid="catalog-issues-panel">
      <SpaceBetween size="m">
        {search.result !== undefined &&
          search.result.dependencyStatus === "PARTIAL" &&
          search.result.warnings.length > 0 && (
            <Alert type="warning" data-testid="catalog-issues-partial-alert">
              {search.result.warnings.join(" ")}
            </Alert>
          )}
        {search.result !== undefined && (
          <CatalogIssuesSummary
            listedAsinCount={filteredRows.length}
            summary={search.result.summary}
          />
        )}
        <CatalogIssuesTable
          items={filteredRows}
          loading={search.status === "loading"}
          filteringText={filteringText}
          onFilteringChange={setFilteringText}
          selectedItems={selectedItems}
          onSelectionChange={onSelectionChange}
        />
        {details.length > 0 && (
          <Container
            data-testid="catalog-issue-details"
            header={
              <Header variant="h3">
                {`Issue Details (${details.length} ASIN${
                  details.length === 1 ? "" : "s"
                } selected)`}
              </Header>
            }
          >
            <SpaceBetween size="l">
              {details.map((detail) => (
                <CatalogIssueDetailPane
                  key={`${detail.asin}/${detail.marketplace}`}
                  detail={detail}
                />
              ))}
            </SpaceBetween>
          </Container>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default CatalogIssuesPanel;
